import * as Sentry from '@sentry/react';
import { hasConsent, OneTrustConsent, onFirstConsent } from '@squareup/service-one-trust';
import { getSession } from '@squareup/service-session';
import { Environment, getEnvironment } from '@squareup/util-environment';
import { isBrowser } from '@squareup/util-isomorphic';

const DASHBOARD_WEB_CLIENT_SENTRY_DSN = "https://7a8e809e0842eb3041d07653476ff659@o160250.ingest.us.sentry.io/4508064745586688";
const FAKE_EVENT_ID = "test";
const LOW_SAMPLE_RATE = 0.01;
const LOW_SAMPLE_RATE_PATTERNS = [
  /^Missing flag value: /
  // These errors are usually caused during feature setup, occur extremely high volume, and do not affect the user as we gracefully fail closed.
];
function shouldSampleOut(event) {
  const message = event.message ?? event.exception?.values?.[0]?.value;
  if (!message) {
    return false;
  }
  if (LOW_SAMPLE_RATE_PATTERNS.some((pattern) => pattern.test(message))) {
    return Math.random() >= LOW_SAMPLE_RATE;
  }
  return false;
}
const LOCAL_EXCEPTION_GROUP_STYLING = "color: white; background-color: red; padding: 2px";
const LOCAL_MESSAGE_GROUP_STYLING = "color: white; background-color: chocolate; padding: 2px";
class ErrorTrackingClient {
  environment;
  session;
  browserClient;
  // Base scope object directly as provided by Sentry SDK
  #rawScope;
  // Proxy to #rawScope that wraps some methods in custom logic
  scope;
  constructor({
    environment = getEnvironment(),
    session,
    release,
    dsn = DASHBOARD_WEB_CLIENT_SENTRY_DSN,
    tags,
    ignoreErrors,
    normalizeDepth = 3,
    transport,
    enableGlobalIntegrations = false,
    beforeSendCallback
  } = {}) {
    this.environment = environment;
    this.session = session ?? getSession();
    if (!transport) {
      transport = (transportOptions) => Sentry.makeFetchTransport(transportOptions);
    }
    const integrationsToFilter = enableGlobalIntegrations ? [] : ["BrowserApiErrors", "Breadcrumbs", "GlobalHandlers"];
    const integrations = Sentry.getDefaultIntegrations({}).filter(
      (defaultIntegration) => !integrationsToFilter.includes(defaultIntegration.name)
    );
    this.browserClient = new Sentry.BrowserClient({
      dsn,
      integrations,
      ignoreErrors,
      enabled: environment !== Environment.Test,
      stackParser: Sentry.defaultStackParser,
      environment: this.environment,
      release,
      normalizeDepth,
      transport,
      attachStacktrace: true,
      // Allow captureMessage to have stack traces so that ownership rules may target them
      beforeSend(event, hint) {
        if (environment === Environment.Test) {
          return null;
        }
        if (environment !== Environment.Production && environment !== Environment.Staging && environment !== Environment.Sandbox) {
          const shouldSendToSentry = new URL(window.location.href).searchParams.get("sendToSentry");
          if (event.message) {
            console.groupCollapsed(
              `%cSentry Message (${event.level}): ${event.message}`,
              LOCAL_MESSAGE_GROUP_STYLING
            );
            console.info("Event:", event);
            console.info("Hint:", hint);
            if (!shouldSendToSentry) {
              console.warn("Errors in development environment aren't sent to Sentry unless the `sendToSentry` query param is set.");
            }
            console.groupEnd();
          } else {
            const error = event?.exception?.values?.[0];
            console.groupCollapsed(`%cSentry Exception: ${error?.type} \u2014 ${error?.value}`, LOCAL_EXCEPTION_GROUP_STYLING);
            console.info("Error:", error);
            console.info("Event:", event);
            console.info("Hint:", hint);
            if (!shouldSendToSentry) {
              console.warn("Errors in development environment aren't sent to Sentry unless the `sendToSentry` query param is set.");
            }
            console.groupEnd();
          }
          if (!shouldSendToSentry) {
            return null;
          }
        }
        if (!hasConsent(OneTrustConsent.PERFORMANCE)) {
          return null;
        }
        if (shouldSampleOut(event)) {
          return null;
        }
        if (beforeSendCallback) {
          return beforeSendCallback(event, hint);
        }
        return event;
      }
    });
    this.#rawScope = new Sentry.Scope();
    this.#rawScope.setClient(this.browserClient);
    this.scope = this.#createScopeProxy(this.#rawScope);
    if (this.session) {
      this.setUser({
        id: this.session.merchantToken,
        merchantToken: this.session.merchantToken,
        employmentToken: this.session.employmentToken
      });
      if (this.session.merchantToken) {
        this.setTags({
          merchantToken: this.session.merchantToken
        });
      }
    }
    if (tags) {
      this.scope.setTags(tags);
    }
    if (isBrowser()) {
      onFirstConsent(OneTrustConsent.PERFORMANCE, () => {
        this.browserClient.init();
      });
    }
  }
  /**
   * Runs callback in the context of a cloned scope that is discarded after execution. Callback should ideally call
   * methods such as `captureException` on the passed scope parameter; however, calling the methods directly on the
   * client instance in the callback is supported so long as the callback is synchronous.
   */
  withScope(callback) {
    const originalScopeProxy = this.scope;
    const clonedRawScope = this.#rawScope.clone();
    const clonedScopeProxy = this.#createScopeProxy(clonedRawScope);
    this.scope = clonedScopeProxy;
    try {
      return callback(clonedScopeProxy);
    } finally {
      this.scope = originalScopeProxy;
    }
  }
  setUser = (...args) => this.scope.setUser(...args);
  setTags = (...args) => this.scope.setTags(...args);
  setTag = (...args) => this.scope.setTag(...args);
  setFingerprint = (...args) => this.scope.setFingerprint(...args);
  addBreadcrumb = (...args) => this.scope.addBreadcrumb(...args);
  // Maintaining this odd-named method for backward compatibility; not sure if used.
  breadCrumbs = this.addBreadcrumb;
  captureException = (...args) => this.scope.captureException(...args);
  captureMessage = (...args) => this.scope.captureMessage(...args);
  #createScopeProxy(baseScope) {
    const overrides = {
      captureException: ((error, hint) => {
        if (this.environment === Environment.Test) {
          return FAKE_EVENT_ID;
        }
        return baseScope.captureException(error, hint);
      }),
      captureMessage: ((message, level, hint) => {
        if (this.environment === Environment.Test) {
          return FAKE_EVENT_ID;
        }
        let scope = baseScope;
        if (hint?.data) {
          scope = baseScope.clone();
          scope.setExtras(hint.data);
        }
        return scope.captureMessage(message, level, hint);
      })
    };
    return new Proxy(baseScope, {
      get(target, prop, receiver) {
        if (prop in overrides) {
          return overrides[prop];
        }
        const value = Reflect.get(target, prop, receiver);
        return typeof value === "function" ? value.bind(target) : value;
      }
    });
  }
}

export { ErrorTrackingClient as E };
